import React from 'react';
import { FaCheck } from 'react-icons/fa';
import { getStaticContent } from '../constants/staticContent';

const WhySection = ({ language = 'en' }) => {
  const { WHY_CONTENT } = getStaticContent(language);
  return (
    <section id="why" className="min-h-screen flex items-center bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-16 px-4 md:px-8">
      <div className="max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-block mb-4">
            <span className="text-xs font-bold tracking-widest uppercase text-emerald-700 bg-emerald-100 px-4 py-1.5 rounded-full border border-emerald-300">
              {WHY_CONTENT.sectionLabel}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
            {WHY_CONTENT.sectionTitle}
          </h2>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            {WHY_CONTENT.description}
          </p>
        </div>

        {/* Problem Statement */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-xl p-6 md:p-8 mb-12 md:mb-16 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center flex-shrink-0">
              {/* eslint-disable-next-line react/jsx-pascal-case */}
              <WHY_CONTENT.problem.icon className="text-red-600 w-8 h-8 md:w-10 md:h-10" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                {WHY_CONTENT.problem.title}
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                {WHY_CONTENT.problem.description}
              </p>
            </div>
          </div>
        </div>

        {/* Primary and Secondary Purpose */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          {WHY_CONTENT.purposes.map((purpose, index) => (
            <div
              key={index}
              className={`group rounded-2xl p-6 md:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-4 ${
                purpose.type === 'primary' 
                  ? 'bg-gradient-to-br from-emerald-500 to-green-600 border-emerald-300' 
                  : 'bg-gradient-to-br from-blue-500 to-indigo-600 border-blue-300'
              }`}
            >
              {/* Icon */}
              <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <purpose.icon className="text-white w-10 h-10 md:w-12 md:h-12" />
              </div>
              
              {/* Title */}
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                {purpose.title}
              </h3>
              
              {/* Description */}
              <p className="text-sm md:text-base text-white/90 leading-relaxed mb-6">
                {purpose.description}
              </p>
              
              {/* Highlights */}
              <div className="space-y-2">
                {purpose.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-white">
                    <FaCheck className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                    <span className="text-sm md:text-base leading-relaxed">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Core Values Grid */}
        <div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
            {WHY_CONTENT.valuesHeading}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_CONTENT.values.map((value, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-gray-100 hover:border-emerald-300"
              >
                {/* Icon */}
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-3 transform group-hover:scale-110 transition-transform duration-300 bg-emerald-50 rounded-2xl">
                  <value.icon className="text-emerald-600 w-6 h-6 md:w-8 md:h-8" />
                </div>
                
                {/* Title */}
                <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {value.title}
                </h4>
                
                {/* Description */}
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default WhySection;
